/**
 * @description Security validation utilities for detecting potentially malicious code
 * @author Security Enhancement
 */

export interface SecurityViolation {
  pattern: RegExp;
  messageKey: string;
  severity: "error" | "warning";
}

export interface ValidationResult {
  messageKey: string;
  severity: "error" | "warning";
}

/**
 * Security patterns to detect potentially malicious code
 * These patterns help prevent XSS, cookie theft, and script injection attacks
 */
export const SECURITY_PATTERNS: SecurityViolation[] = [
  // Cookie access patterns
  {
    pattern: /(?:\b[A-Za-z_$][\w$]*|\]|\))\s*(?:\.\s*|\?\.\s*)cookie\b/gi,
    messageKey: "common.securityValidation.cookieAccess",
    severity: "error",
  },
  {
    pattern: /(?:\b[A-Za-z_$][\w$]*|\]|\))\s*\[\s*(?:['"`])\s*cookie\s*(?:['"`])\s*\]/gi,
    messageKey: "common.securityValidation.cookieAccess",
    severity: "error",
  },
  // storage read access pattern
  {
    pattern:
      /(?:\b(?:window|self|globalThis)\s*\.\s*)?(?:localStorage|sessionStorage)\s*(?:\.\s*|\?\.\s*)getItem\b/gi,
    messageKey: "common.securityValidation.storageAccess",
    severity: "error",
  },
  {
    pattern:
      /(?:\b(?:window|self|globalThis)\s*\.\s*)?(?:localStorage|sessionStorage)\s*\[\s*(?:['"`])\s*getItem\s*(?:['"`])\s*\]\s*\(/gi,
    messageKey: "common.securityValidation.storageAccess",
    severity: "error",
  },
  // Dangerous JavaScript execution
  {
    pattern: /(?:\b(?:window|self|globalThis)\s*\.\s*)?eval\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /(window|self|globalThis|this)\s*\[\s*['"]eval['"]\s*\]\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /frames\s*\[\s*\d+\s*\]\s*(?:\.\s*eval|\[\s*['"]eval['"]\s*\])\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /\(\s*0\s*,\s*eval\s*\)\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /eval\s*\.\s*(call|apply)\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /\bFunction\b\s*(?:\.\s*constructor|\[\s*['"]constructor['"]\s*\])\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern:
      /[\w$)\]]\s*(?:\.\s*constructor|\[\s*['"]constructor['"]\s*\])\s*(?:\.\s*constructor|\[\s*['"]constructor['"]\s*\])\s*\(/gi,
    messageKey: "common.securityValidation.evalUsage",
    severity: "error",
  },
  {
    pattern: /(?:^|\W)(?:new\s+)?Function\s*\(/gi,
    messageKey: "common.securityValidation.dynamicFunction",
    severity: "error",
  },
  {
    pattern:
      /(?:\b(?:window|self|globalThis)\s*\.\s*)?setTimeout\s*\(\s*(?:\/\*[\s\S]*?\*\/\s*)*\\?['"`]/gi,
    messageKey: "common.securityValidation.stringExecution",
    severity: "error",
  },
  {
    pattern:
      /(?:\b(?:window|self|globalThis)\s*\.\s*)?setInterval\s*\(\s*(?:\/\*[\s\S]*?\*\/\s*)*\\?['"`]/gi,
    messageKey: "common.securityValidation.stringExecution",
    severity: "error",
  },
];

/**
 * @description Validates code input for security vulnerabilities
 * @param code - The code string to validate
 * @param patterns - Optional custom patterns (defaults to SECURITY_PATTERNS)
 * @returns Array of detected security violations with message keys
 */
export const validateCodeSecurity = (
  code: string,
  patterns: SecurityViolation[] = SECURITY_PATTERNS
): ValidationResult[] => {
  const violations: ValidationResult[] = [];
  const seenMessages = new Set<string>();

  for (const { pattern, messageKey, severity } of patterns) {
    if (pattern.test(code) && !seenMessages.has(messageKey)) {
      violations.push({ messageKey, severity });
      seenMessages.add(messageKey);
    }
    // Reset regex lastIndex for global patterns
    pattern.lastIndex = 0;
  }

  return violations;
};

/**
 * @description Checks if code has any security errors (blocking issues)
 * @param code - The code string to validate
 * @returns Boolean indicating if there are security errors
 */
export const hasSecurityErrors = (code: string): boolean => {
  const violations = validateCodeSecurity(code);
  return violations.some((v) => v.severity === "error");
};

/**
 * @description Checks if code has any security warnings (non-blocking issues)
 * @param code - The code string to validate
 * @returns Boolean indicating if there are security warnings
 */
export const hasSecurityWarnings = (code: string): boolean => {
  const violations = validateCodeSecurity(code);
  return violations.some((v) => v.severity === "warning");
};
